<template>
  <div
    class="product-card-sample"
    :class="{
      [$class('loading')]: !product,
      'product-card--studioshot': product?.tags?.includes('studioshot'),
    }"
  >
    <a class="product-card-sample__link" :href="variant.url" tabindex="-1">
      <span class="visually-hidden" v-text="product.title" />
      <div class="product-card-sample__image-container">
        <responsive-image
          :class="
            !variant.available || !product.available ? 'opacity-reduced' : ''
          "
          class="product-card-sample__image"
          :image="featuredImage"
          :ratio="$variable('product.imageRatio')"
          sizes="53px"
          widths="53, 106, 159"
        />
      </div>
    </a>

    <div class="product-card-sample__info">
      <a
        class="line-item__link product-card-sample__title text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold"
        :href="variant.url"
        v-text="product.title"
      />

      <p
        v-if="subtitle"
        class="product-card-sample__subtitle text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        v-text="subtitle"
      />

      <product-prices
        class="product-card-sample__prices"
        :compare-at-price="variant.compareAtPrice?.amount"
        :discount-logic="discountLogic"
        :price="variant.price?.amount"
        template="samples"
        :unit-price="variant.unitPrice?.amount"
        :unit-price-measurement="variant?.unitPriceMeasurement"
      />
    </div>

    <button
      v-if="product"
      class="product-card-sample__add-to-cart"
      :class="{ [$class('success')]: state === 'added' }"
      :disabled="disabled || !variant.available || !product.available"
      type="button"
      @click="handleAddToCart"
    >
      <span
        class="visually-hidden"
        v-text="$string('product.cta.add_to_cart')"
      />

      <component :is="addToCartIcon" />
    </button>
  </div>
</template>

<script>
/**
 * Vue: Product card sample (product-card-sample)
 * -----------------------------------------------------------------------------
 * Small product cart with add to cart for product samples.
 *
 * @param {Boolean|Object} product - Formatted product object.
 * @param {String} state - Cart samples state.
 * @param {Boolean|Object} variant - Selected variant.
 *
 * @emits emitError - Add to cart error message.
 */
import { mapActions, mapState } from 'pinia';
import useOverlaysStore from '~stores/overlays';

import ProductPrices from '~global/product-prices/product-prices';
import ResponsiveImage from '~global/images/responsive-image';

import IconAdd from '~icons/general/add.svg';
import IconCheck from '~icons/general/check.svg';
import IconLoading from '~icons/general/loading.svg';

import { computeDiscountLogic } from '~/helpers/vue';

import { formatTrackingItem } from '~/format/format-tracking-item';
import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

export default {
  name: 'ProductCardSample',

  components: {
    IconAdd,
    IconCheck,
    IconLoading,
    ProductPrices,
    ResponsiveImage,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    disabled: {
      type: [Boolean, Object],
      default: false,
    },
  },

  emits: {
    'emit-error': () => true,
  },

  data() {
    return {
      error: '',
      state: 'ready',
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
    }),
    /**
     * Compute add to cart icon.
     * @returns {String}
     */
    addToCartIcon() {
      switch (this.state) {
        case 'adding':
          return 'icon-loading';
        case 'added':
          return 'icon-check';
        default:
          return 'icon-add';
      }
    },

    /**
     * Compute subtitle.
     * @returns {String}
     */
    subtitle() {
      if (this.variant?.title && this.variant?.title !== 'Default Title') {
        return this.variant.title;
      }

      return this.product?.subtitle || '';
    },

    /**
     * Compute featured image.
     * @returns {Object}
     */
    featuredImage() {
      if (this.variant?.localizedImage) {
        return this.variant.localizedImage;
      }

      if (this.variant?.featuredImage) {
        return this.variant.featuredImage;
      }

      return this.product.featuredImage;
    },

    /**
     * Compute discount logic.
     * @returns {Object}
     */
    discountLogic() {
      return computeDiscountLogic({
        discountPreview: false,
        onSale: this.variant?.compareAtPriceDiscount,
        product: this.product,
        variant: this.variant,
      });
    },
  },

  watch: {
    /**
     * Watch error message to emit.
     */
    error() {
      this.$emit('emit-error', this.error);
    },
  },

  methods: {
    ...mapActions(useCartStore, {
      addItem: 'addItem',
      getCartAttributeCode: 'getCartAttributeCode',
    }),
    ...mapActions(useOverlaysStore, {
      openOverlay: 'open',
    }),

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant) {
        this.error = this.$string('product.form.select_variant');
        return;
      }

      if (this.state !== 'ready') {
        return;
      }

      this.error = '';
      this.state = 'adding';

      const items = [
        {
          id: this.variant.id,
          quantity: 1,
          hasLocalizedImage: Boolean(this.variant.localizedImage),
        },
      ];

      /**
       * Add to cart.
       */
      try {
        await this.addItem(items);
        this.state = 'added';

        // Tracking event add qty to item in cart
        this.handleTrackingAddItem();

        this.openOverlay({
          component: 'cart-drawer',
          ignoreDismissed: true,
          namespace: 'cartDrawer',
        });

        setTimeout(() => {
          this.state = 'ready';
        }, this.$timing('addedToCart'));
      } catch (error) {
        this.state = 'error';

        if (error.description) {
          this.handleErrorMessage(error.description);
          return;
        }

        cnvs.ReportError('Failed to add product sample to cart', error);
      }
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof errorDescription === 'string') {
        this.error = errorDescription;
        return;
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`;
        })
        .join('<br>');
    },

    /**
     * Tracking event add qty to item in cart.
     */
    handleTrackingAddItem() {
      const campaignDiscount = this.getCampaignDiscount(
        this.discountLogic,
        'active',
      );
      const discountObject = {
        discountAmount: campaignDiscount?.amount,
        calculationType: campaignDiscount?.calculationType,
        discountCode: this.getCartAttributeCode(),
      };
      const deliveryTime = this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
      const currencyCode = this.$variable('store.currency');

      const item = formatTrackingItem({
        product: this.product,
        variant: this.variant,
        template: 'Cart Drawer Samples',
        discountObject,
        deliveryTime,
        currencyCode,
        quantity: this.quantity,
      });

      cnvs.EventBus.emit('dataLayer:add_to_cart', {
        event: 'add_to_cart',
        currency: cnvs.store.currency,
        location: 'Cart Drawer Samples',
        items: [item],
      });
    },
  },
};
</script>

<style lang="scss">
@import './product-card-sample';
</style>
